import React, { useEffect, useState } from 'react';
import { COUNTRIES } from '../../../constants/countries';

export default function BusinessLocationStep({ data, onChange, validateTrigger = 0 }) {
  const [formData, setFormData] = useState(data || {});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  useEffect(() => {
    if (!validateTrigger) return;
    validate(data || {});
  }, [validateTrigger]);

  const validate = (nextData) => {
    const nextErrors = {};
    const streetAddress = String(nextData.streetAddress || '').trim();
    if (!streetAddress) {
      nextErrors.streetAddress = 'Street address is required';
    } else if (streetAddress.includes(',')) {
      nextErrors.streetAddress = 'Enter only street and house number here (no city/postal/country).';
    }
    if (!String(nextData.city || '').trim()) nextErrors.city = 'City is required';
    if (!String(nextData.zipCode || '').trim()) nextErrors.zipCode = 'Postal/ZIP code is required';
    if (!/^[A-Za-z]{2}$/.test(String(nextData.countryCode || '').trim())) nextErrors.countryCode = 'Country is required';
    if (!String(nextData.fullAddress || '').trim()) nextErrors.fullAddress = 'Full address is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateField = (patch) => {
    const updated = { ...formData, ...patch };
    setFormData(updated);
    onChange(updated);
    if (Object.keys(errors).length) {
      validate(updated);
    }
  };

  const inputBaseClass =
    'mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:ring-2 focus:ring-indigo-100';
  const inputClass = (hasError) =>
    `${inputBaseClass} ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : 'border-gray-300 focus:border-indigo-600'}`;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Add your customer-facing mailing location. For best accuracy, separate street, city, district/area, and postal code.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700">Street Address <span className="text-red-500">*</span></label>
        <input
          type="text"
          value={formData.streetAddress || ''}
          onChange={(e) => updateField({ streetAddress: e.target.value })}
          className={inputClass(!!errors.streetAddress)}
          placeholder="Street and number only (e.g. Megalou Alexandrou 38)"
        />
        {errors.streetAddress && <p className="mt-1 text-sm text-red-600">{errors.streetAddress}</p>}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Apartment / Unit</label>
          <input
            type="text"
            value={formData.apartmentOrUnit || ''}
            onChange={(e) => updateField({ apartmentOrUnit: e.target.value })}
            className={inputClass(false)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Floor</label>
          <input
            type="text"
            value={formData.floorNumber || ''}
            onChange={(e) => updateField({ floorNumber: e.target.value })}
            className={inputClass(false)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">City <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => updateField({ city: e.target.value })}
            className={inputClass(!!errors.city)}
          />
          {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">District / Area</label>
          <input
            type="text"
            value={formData.stateProvince || ''}
            onChange={(e) => updateField({ stateProvince: e.target.value })}
            className={inputClass(false)}
            placeholder="e.g. Paliometocho / Paliometoxo"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Postal / ZIP Code <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={formData.zipCode || ''}
            onChange={(e) => updateField({ zipCode: e.target.value })}
            className={inputClass(!!errors.zipCode)}
          />
          {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Country <span className="text-red-500">*</span></label>
        <select
          value={formData.countryCode || ''}
          onChange={(e) => updateField({ countryCode: e.target.value })}
          className={inputClass(!!errors.countryCode)}
        >
          <option value="">Select country</option>
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        {errors.countryCode && <p className="mt-1 text-sm text-red-600">{errors.countryCode}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Full Address <span className="text-red-500">*</span></label>
        <textarea
          value={formData.fullAddress || ''}
          onChange={(e) => updateField({ fullAddress: e.target.value })}
          className={`${inputClass(!!errors.fullAddress)} min-h-[90px] resize-y`}
          placeholder="Full mailing address"
        />
        {errors.fullAddress && <p className="mt-1 text-sm text-red-600">{errors.fullAddress}</p>}
      </div>

    </div>
  );
}
